import React, { useState, useEffect } from 'react';
import ElitureGoals from './ElitureGoals';
import ElitureVideoPlayer from './ElitureVideoPlayer';
import ElitureMockExam from './ElitureMockExam';
import ElitureAdminPanel from './ElitureAdminPanel';
import api, { toArr } from '../api';

const C = {
  primary: '#6366f1', secondary: '#10b981', accent: '#f59e0b',
  bg: 'rgba(255,255,255,0.04)', bgHover: 'rgba(255,255,255,0.08)',
  border: 'rgba(255,255,255,0.08)', text: '#fff',
  textMuted: 'rgba(255,255,255,0.5)', textSub: 'rgba(255,255,255,0.7)',
  card: 'rgba(255,255,255,0.05)', cardBorder: 'rgba(99,102,241,0.15)',
};

// Role-based nav — Admin Panel and Content Manager gated by role
const getNavItems = (role) => {
  const items = [
    { key: 'dashboard',    label: 'Dashboard',       icon: '⊞' },
    { key: 'courses',      label: 'My Courses',       icon: '▶' },
    { key: 'goals',        label: 'Goals',            icon: '◎' },
    { key: 'mocktest',     label: 'Mock Tests',       icon: '✦' },
    { key: 'analytics',    label: 'Analytics',        icon: '◈' },
    { key: 'certificates', label: 'Certificates',     icon: '🏆' },
    { key: 'profile',      label: 'My Profile',       icon: '◉' },
  ];
  if (role === 'instructor' || role === 'admin') {
    items.push({ key: 'manage', label: 'Manage Content', icon: '⊕' });
  }
  if (role === 'admin') {
    items.push({ key: 'admin', label: 'Admin Panel', icon: '⚙' });
  }
  return items;
};

// ── Shared sub-components ────────────────────────────────────────────────────

const StatCard = ({ label, value, icon, color, trend, loading }) => (
  <div style={{
    background: C.card, border: `1px solid ${C.cardBorder}`,
    borderRadius: 16, padding: '24px', position: 'relative', overflow: 'hidden',
    transition: 'all 0.3s ease', cursor: 'default',
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = color; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = C.cardBorder; }}
  >
    <div style={{ position: 'absolute', top: 0, right: 0, width: 120, height: 120,
      background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`, borderRadius: '0 16px 0 0' }} />
    <div style={{ fontSize: 28, marginBottom: 12 }}>{icon}</div>
    {loading
      ? <div style={{ height: 36, width: 64, background: 'rgba(255,255,255,0.1)', borderRadius: 8, marginBottom: 4 }} />
      : <div style={{ fontSize: '2rem', fontWeight: 800, color: C.text, letterSpacing: '-1px' }}>{value}</div>
    }
    <div style={{ fontSize: '0.8rem', color: C.textMuted, marginTop: 4, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
    {trend && <div style={{ marginTop: 8, fontSize: '0.75rem', color: C.secondary, fontWeight: 600 }}>↑ {trend} this week</div>}
  </div>
);

const ProgressBar = ({ label, value, color }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
      <span style={{ fontSize: '0.85rem', color: C.textSub }}>{label}</span>
      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: C.text }}>{value}%</span>
    </div>
    <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 100, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${value}%`,
        background: `linear-gradient(90deg, ${color || C.primary}, ${C.secondary})`,
        borderRadius: 100, transition: 'width 1s ease', boxShadow: `0 0 8px ${color || C.primary}60` }} />
    </div>
  </div>
);

// ── Dashboard Home ────────────────────────────────────────────────────────────

const DashboardHome = ({ user }) => {
  const [stats, setStats] = useState({ enrolled: 0, hoursLearned: 0, certificates: 0, mockTests: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [recentActivity] = useState([
    { label: 'Completed GenAI Module 3', time: '2h ago', icon: '🎯', color: '#6366f1' },
    { label: 'Scored 85% on Mock Test', time: 'Yesterday', icon: '⭐', color: '#f59e0b' },
    { label: '7-Day Learning Streak', time: '3 days ago', icon: '🔥', color: '#ef4444' },
    { label: 'Certificate Earned', time: '1 week ago', icon: '🏅', color: '#10b981' },
  ]);

  useEffect(() => {
    if (!user?.id || user.id === 'demo-user-id') {
      setStats({ enrolled: 12, hoursLearned: 48, certificates: 3, mockTests: 7 });
      setStatsLoading(false);
      return;
    }
    api.get(`/users/${user.id}/analytics`)
      .then(res => {
        const d = res.data || {};
        setStats({
          enrolled: d.enrolled ?? 12,
          hoursLearned: d.totalHours ?? 48,
          certificates: d.certificates ?? 3,
          mockTests: d.totalSessions ?? 7,
        });
      })
      .catch(() => setStats({ enrolled: 12, hoursLearned: 48, certificates: 3, mockTests: 7 }))
      .finally(() => setStatsLoading(false));
  }, [user]);

  const greeting = user?.role === 'admin'
    ? 'Manage your institution from the Admin Panel.'
    : user?.role === 'instructor'
      ? 'Track your students and manage course content.'
      : 'Continue your learning journey — stay consistent!';

  return (
    <div>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(16,185,129,0.15) 100%)',
        border: '1px solid rgba(99,102,241,0.2)',
        borderRadius: 20, padding: '32px 36px', marginBottom: 32, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: -20, top: -20, width: 200, height: 200,
          background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)' }} />
        <div style={{ fontSize: '0.75rem', color: C.secondary, fontWeight: 600,
          letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Welcome back</div>
        <div style={{ fontSize: '2rem', fontWeight: 800, color: '#fff', marginBottom: 8 }}>
          Hello, {user?.name?.split(' ')[0] || 'Learner'} 👋
        </div>
        <div style={{ fontSize: '0.9rem', color: C.textSub, maxWidth: 500 }}>{greeting}</div>
        <div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
            border: 'none', borderRadius: 10, padding: '10px 22px', color: '#fff',
            fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
            boxShadow: '0 4px 15px rgba(99,102,241,0.4)' }}>Continue Learning</button>
          <button style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 10, padding: '10px 22px', color: '#fff',
            fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}>View Schedule</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard label="Courses Enrolled" value={stats.enrolled} icon="📚" color={C.primary} trend="2 new" loading={statsLoading} />
        <StatCard label="Hours Learned"    value={stats.hoursLearned} icon="⏱" color={C.secondary} trend="5.2h" loading={statsLoading} />
        <StatCard label="Certificates"     value={stats.certificates} icon="🏆" color={C.accent} loading={statsLoading} />
        <StatCard label="Mock Tests"       value={stats.mockTests} icon="✅" color="#ec4899" trend="1 passed" loading={statsLoading} />
      </div>

      {/* Progress & Activity */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 24 }}>
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 20, fontSize: '0.95rem' }}>Learning Progress</div>
          <ProgressBar label="GenAI Certification"  value={72} color="#6366f1" />
          <ProgressBar label="Cloud Architecture"   value={45} color="#10b981" />
          <ProgressBar label="Machine Learning"      value={88} color="#f59e0b" />
          <ProgressBar label="DevOps Essentials"     value={30} color="#ec4899" />
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 24 }}>
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 20, fontSize: '0.95rem' }}>Recent Activity</div>
          {recentActivity.map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'center' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${a.color}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                {a.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.85rem', color: C.textSub, fontWeight: 500 }}>{a.label}</div>
                <div style={{ fontSize: '0.72rem', color: C.textMuted }}>{a.time}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Analytics ─────────────────────────────────────────────────────────────────

const AnalyticsSection = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const data = [40, 65, 55, 80, 72, 90];
  const maxVal = Math.max(...data);

  return (
    <div>
      <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: 24 }}>Learning Analytics</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 20 }}>
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28 }}>
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 24, fontSize: '0.95rem' }}>Monthly Learning Hours</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', height: 160 }}>
            {data.map((val, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: '0.7rem', color: C.textMuted }}>{val}h</div>
                <div style={{ width: '100%', height: `${(val / maxVal) * 130}px`,
                  background: i === data.length - 1 ? 'linear-gradient(180deg, #6366f1, #10b981)' : 'rgba(99,102,241,0.3)',
                  borderRadius: '6px 6px 0 0', transition: 'all 0.5s ease',
                  boxShadow: i === data.length - 1 ? '0 0 15px rgba(99,102,241,0.4)' : 'none' }} />
                <div style={{ fontSize: '0.7rem', color: C.textMuted }}>{months[i]}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[
            { label: 'Avg. Daily Study',  value: '2.4h', icon: '⏰', color: C.primary },
            { label: 'Completion Rate',   value: '78%',  icon: '✅', color: C.secondary },
            { label: 'Quiz Score Avg',    value: '82%',  icon: '📝', color: C.accent },
          ].map((s, i) => (
            <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`,
              borderRadius: 14, padding: 20, display: 'flex', gap: 14, alignItems: 'center' }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: `${s.color}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff' }}>{s.value}</div>
                <div style={{ fontSize: '0.73rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28 }}>
        <div style={{ fontWeight: 700, color: '#fff', marginBottom: 20, fontSize: '0.95rem' }}>Skills Progress</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '8px 32px' }}>
          {[
            { label: 'Generative AI',      value: 72, color: '#6366f1' },
            { label: 'Cloud Computing',    value: 45, color: '#10b981' },
            { label: 'Machine Learning',   value: 88, color: '#f59e0b' },
            { label: 'DevOps',             value: 30, color: '#ec4899' },
            { label: 'Data Engineering',   value: 55, color: '#06b6d4' },
            { label: 'Prompt Engineering', value: 65, color: '#8b5cf6' },
          ].map((s, i) => <ProgressBar key={i} label={s.label} value={s.value} color={s.color} />)}
        </div>
      </div>
    </div>
  );
};

// ── Profile ───────────────────────────────────────────────────────────────────

const ProfileSection = ({ user }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id || user.id === 'demo-user-id') {
      setAnalytics({ totalSessions: 7, avgPercentage: 82, totalHours: 48, certificates: 3 });
      setLoading(false);
      return;
    }
    api.get(`/users/${user.id}/analytics`)
      .then(res => setAnalytics(res.data))
      .catch(() => setAnalytics({ totalSessions: 7, avgPercentage: 82, totalHours: 48, certificates: 3 }))
      .finally(() => setLoading(false));
  }, [user]);

  const roleColors = { admin: '#ef4444', instructor: '#f59e0b', learner: '#10b981' };
  const roleColor = roleColors[user?.role] || C.primary;

  return (
    <div>
      <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: 28 }}>My Profile</div>

      {/* Identity card */}
      <div style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(16,185,129,0.1))',
        border: '1px solid rgba(99,102,241,0.25)', borderRadius: 20, padding: 32, marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1, #10b981)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32, fontWeight: 800, color: '#fff',
            boxShadow: '0 0 30px rgba(99,102,241,0.4)', flexShrink: 0 }}>
            {(user?.name || 'U').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff', marginBottom: 4 }}>{user?.name}</div>
            <div style={{ fontSize: '0.875rem', color: C.textMuted, marginBottom: 12 }}>{user?.email}</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: roleColor,
                background: `${roleColor}20`, border: `1px solid ${roleColor}40`,
                padding: '4px 12px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: 1 }}>
                {user?.role || 'Learner'}
              </span>
              {user?.id === 'demo-user-id' && (
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#f59e0b',
                  background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)',
                  padding: '4px 12px', borderRadius: 20, letterSpacing: 1 }}>DEMO MODE</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Mock Tests',   value: analytics?.totalSessions, icon: '📝', color: C.primary },
          { label: 'Avg. Score',   value: analytics?.avgPercentage != null ? `${Math.round(analytics.avgPercentage)}%` : null, icon: '📊', color: C.secondary },
          { label: 'Hours Learned', value: analytics?.totalHours,   icon: '⏱', color: C.accent },
          { label: 'Certificates', value: analytics?.certificates,  icon: '🏆', color: '#ec4899' },
        ].map((s, i) => (
          <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`,
            borderRadius: 14, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>{s.icon}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff' }}>
              {loading ? '—' : (s.value ?? '—')}
            </div>
            <div style={{ fontSize: '0.72rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Recommendation */}
      <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 24 }}>
        <div style={{ fontWeight: 700, color: '#fff', marginBottom: 12, fontSize: '0.95rem' }}>Learning Recommendation</div>
        <div style={{ color: C.textSub, fontSize: '0.875rem', lineHeight: 1.6 }}>
          Based on your progress, focus on completing your <strong style={{ color: C.primary }}>Cloud Architecture</strong> track — you're 45% through!
          A consistent 1-hour session daily will get you certified in under 3 weeks.
        </div>
      </div>
    </div>
  );
};

// ── Certificates ──────────────────────────────────────────────────────────────

/** Draw and download a certificate as a PNG using the Canvas API */
function downloadCertificate(cert, user, color) {
  const W = 1200, H = 850;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // ── Background ────────────────────────────────────────────────────────────
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0f0c29'); bg.addColorStop(0.5, '#302b63'); bg.addColorStop(1, '#24243e');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // Top glow
  const glow = ctx.createRadialGradient(W / 2, 0, 0, W / 2, 0, 350);
  glow.addColorStop(0, color + '35'); glow.addColorStop(1, 'transparent');
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H / 2);

  // ── Borders ───────────────────────────────────────────────────────────────
  ctx.strokeStyle = color; ctx.lineWidth = 6;
  roundRect(ctx, 22, 22, W - 44, H - 44, 16);
  ctx.stroke();
  ctx.strokeStyle = color + '40'; ctx.lineWidth = 1.5;
  roundRect(ctx, 40, 40, W - 80, H - 80, 12);
  ctx.stroke();

  // Corner accents
  [[60, 60], [W - 60, 60], [60, H - 60], [W - 60, H - 60]].forEach(([x, y]) => {
    ctx.beginPath(); ctx.arc(x, y, 14, 0, Math.PI * 2);
    ctx.fillStyle = color + '50'; ctx.fill();
    ctx.strokeStyle = color; ctx.lineWidth = 1.5; ctx.stroke();
  });

  // ── Institution line ──────────────────────────────────────────────────────
  ctx.font = 'bold 18px Arial'; ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.fillText('ELITURE LEARNING MANAGEMENT SYSTEM', W / 2, 112);
  ctx.strokeStyle = color + '50'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(260, 128); ctx.lineTo(940, 128); ctx.stroke();

  // ── Title ─────────────────────────────────────────────────────────────────
  ctx.font = 'bold 48px Georgia, serif'; ctx.fillStyle = '#ffffff';
  ctx.fillText('CERTIFICATE OF COMPLETION', W / 2, 208);

  ctx.font = '20px Georgia, serif'; ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.fillText('This is to proudly certify that', W / 2, 268);

  // ── Recipient name ────────────────────────────────────────────────────────
  ctx.font = 'bold 54px Georgia, serif';
  const nameGrad = ctx.createLinearGradient(200, 290, W - 200, 360);
  nameGrad.addColorStop(0, color); nameGrad.addColorStop(1, '#10b981');
  ctx.fillStyle = nameGrad;
  const recipientName = user?.name || 'Learner';
  ctx.fillText(recipientName, W / 2, 360);

  const nw = ctx.measureText(recipientName).width;
  ctx.strokeStyle = color + '55'; ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(W / 2 - nw / 2, 375); ctx.lineTo(W / 2 + nw / 2, 375);
  ctx.stroke();

  // ── Completion text ───────────────────────────────────────────────────────
  ctx.font = '19px Arial'; ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.fillText('has successfully completed all requirements for the', W / 2, 420);

  ctx.font = 'bold 34px Georgia, serif'; ctx.fillStyle = '#ffffff';
  ctx.fillText(`${cert.category} Certification Program`, W / 2, 472);

  ctx.font = 'bold 20px Arial'; ctx.fillStyle = color;
  ctx.fillText(`with a final score of ${cert.percentage}%`, W / 2, 516);

  // ── Divider ───────────────────────────────────────────────────────────────
  ctx.strokeStyle = 'rgba(255,255,255,0.07)'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(100, 560); ctx.lineTo(W - 100, 560); ctx.stroke();

  // ── Dates & cert number ───────────────────────────────────────────────────
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  ctx.font = '12px Arial'; ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.textAlign = 'left'; ctx.fillText('DATE OF ISSUE', 160, 600);
  ctx.font = 'bold 16px Arial'; ctx.fillStyle = '#ffffff';
  ctx.fillText(fmtDate(cert.issuedAt), 160, 622);

  ctx.font = '12px Arial'; ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.textAlign = 'right'; ctx.fillText('VALID UNTIL', W - 160, 600);
  ctx.font = 'bold 16px Arial'; ctx.fillStyle = '#ffffff';
  ctx.fillText(fmtDate(cert.expiresAt), W - 160, 622);

  ctx.font = '11px Arial'; ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.textAlign = 'center'; ctx.fillText('CERTIFICATE NO.', W / 2, 600);
  ctx.font = 'bold 13px monospace'; ctx.fillStyle = color;
  ctx.fillText(cert.certNumber, W / 2, 622);

  // ── Signature lines ───────────────────────────────────────────────────────
  [[220, 380, 700], [820, 380, 1040]].forEach(([lx, rx, cx]) => {
    ctx.strokeStyle = color + '55'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(lx, 720); ctx.lineTo(rx, 720); ctx.stroke();
    ctx.font = '11px Arial'; ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.textAlign = 'center'; ctx.fillText(cx === 700 ? 'AUTHORIZED SIGNATORY' : 'PROGRAM DIRECTOR', cx - 10, 738);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fillText(cx === 700 ? 'Eliture LMS' : 'Eliture Academy', cx - 10, 754);
  });

  // ── Centre seal ───────────────────────────────────────────────────────────
  ctx.beginPath(); ctx.arc(W / 2, 718, 40, 0, Math.PI * 2);
  ctx.fillStyle = color + '1a'; ctx.fill();
  ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.stroke();
  ctx.beginPath(); ctx.arc(W / 2, 718, 33, 0, Math.PI * 2);
  ctx.strokeStyle = color + '60'; ctx.lineWidth = 1; ctx.stroke();
  ctx.font = 'bold 26px Arial'; ctx.fillStyle = color;
  ctx.textAlign = 'center'; ctx.fillText('E', W / 2, 727);
  ctx.font = '8px Arial'; ctx.fillStyle = color + 'aa';
  ctx.fillText('ELITURE', W / 2, 746);

  // ── Footer watermark ──────────────────────────────────────────────────────
  ctx.font = '10px Arial'; ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.textAlign = 'center';
  ctx.fillText('This certificate is digitally generated by Eliture LMS • Verify at eliture.com/verify', W / 2, 820);

  // ── Download ──────────────────────────────────────────────────────────────
  canvas.toBlob(blob => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${cert.certNumber}-${cert.category}-Certificate.png`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }, 'image/png');
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

const CertificatesSection = ({ user }) => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    // Try real API first; fall back to demo certs
    const fetchCerts = async () => {
      try {
        if (user?.id && user.id !== 'demo-user-id') {
          const res = await api.get(`/users/${user.id}/certificates`);
          const list = toArr(res.data);
          if (list.length) { setCerts(list); setLoading(false); return; }
        }
      } catch { /* fall through to demo data */ }
      // Demo / fallback certs
      setCerts([
        { id: 1, category: 'GenAI',  certNumber: 'ELT-2025-001', percentage: 87, issuedAt: '2025-01-15', expiresAt: '2027-01-15' },
        { id: 2, category: 'Cloud',  certNumber: 'ELT-2025-002', percentage: 78, issuedAt: '2025-02-20', expiresAt: '2027-02-20' },
        { id: 3, category: 'ML',     certNumber: 'ELT-2025-003', percentage: 92, issuedAt: '2025-03-01', expiresAt: '2027-03-01' },
      ]);
      setLoading(false);
    };
    fetchCerts();
  }, [user]);

  const catColors = { GenAI: '#6366f1', Cloud: '#10b981', ML: '#f59e0b', DevOps: '#ec4899' };

  const handleDownload = (cert, color) => {
    setDownloading(cert.id);
    // Use setTimeout so the button state updates render before heavy canvas work
    setTimeout(() => {
      try { downloadCertificate(cert, user, color); }
      catch (e) { console.error('Certificate generation failed:', e); }
      finally { setDownloading(null); }
    }, 50);
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff' }}>My Certificates</div>
        <div style={{ fontSize: '0.8rem', color: C.textMuted, marginTop: 4 }}>
          {certs.length} certificate{certs.length !== 1 ? 's' : ''} earned
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: C.textMuted, padding: 40 }}>Loading certificates...</div>
      ) : certs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎓</div>
          <div style={{ color: C.textMuted, fontSize: '0.95rem' }}>No certificates yet.</div>
          <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', marginTop: 8 }}>
            Pass a mock test with 60%+ to earn your first certificate!
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
          {certs.map(cert => {
            const color = catColors[cert.category] || C.primary;
            const isDownloading = downloading === cert.id;
            return (
              <div key={cert.id} style={{
                background: `linear-gradient(135deg, ${color}18, rgba(0,0,0,0.3))`,
                border: `1px solid ${color}40`, borderRadius: 18, padding: 28, position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100,
                  background: `radial-gradient(circle, ${color}30, transparent 70%)` }} />
                <div style={{ fontSize: 36, marginBottom: 12 }}>🏆</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff', marginBottom: 4 }}>
                  {cert.category} Certification
                </div>
                <div style={{ fontSize: '0.8rem', color: C.textMuted, marginBottom: 16 }}>
                  Score: <span style={{ color: C.secondary, fontWeight: 700 }}>{cert.percentage}%</span>
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)',
                  background: 'rgba(0,0,0,0.3)', padding: '4px 10px', borderRadius: 6,
                  display: 'inline-block', marginBottom: 16 }}>
                  {cert.certNumber}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div>
                    <div style={{ fontSize: '0.72rem', color: C.textMuted }}>
                      Issued: {new Date(cert.issuedAt).toLocaleDateString()}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: C.textMuted }}>
                      Valid until: {new Date(cert.expiresAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(cert, color)}
                    disabled={isDownloading}
                    style={{ background: isDownloading ? 'rgba(255,255,255,0.06)' : `${color}20`,
                      border: `1px solid ${color}40`, borderRadius: 8, padding: '8px 16px',
                      color: isDownloading ? 'rgba(255,255,255,0.4)' : color, fontSize: '0.8rem',
                      fontWeight: 600, cursor: isDownloading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {isDownloading ? '⏳ Generating…' : '⬇ Download'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info notice */}
      <div style={{ marginTop: 24, padding: '12px 18px',
        background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)',
        borderRadius: 10, fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>
        💡 Certificates download as a high-resolution PNG image you can share or print. Pass any mock test with 60%+ to earn new certificates.
      </div>
    </div>
  );
};

// ── Content Manager (Instructor / Admin) ─────────────────────────────────────

const ContentManagerSection = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'GenAI', level: 'Beginner', s3Key: '', duration: '' });
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('supabase'); // 'supabase' | 'db'
  const [deleting, setDeleting] = useState(null);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const fetchSupabaseVideos = () => {
    setLoading(true);
    api.get('/videos/supabase-links')
      .then(res => setVideos(toArr(res.data)))
      .catch(() => {
        showMsg('error', 'Could not load Supabase videos. Check backend connection.');
        setVideos([]);
      })
      .finally(() => setLoading(false));
  };

  const fetchDbVideos = () => {
    setLoading(true);
    api.get('/videos')
      .then(res => setVideos(toArr(res.data)))
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (activeTab === 'supabase') fetchSupabaseVideos();
    else fetchDbVideos();
  }, [activeTab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/videos', form);
      showMsg('success', 'Video registered successfully!');
      setForm({ title: '', description: '', category: 'GenAI', level: 'Beginner', s3Key: '', duration: '' });
      setShowForm(false);
      fetchDbVideos();
      setActiveTab('db');
    } catch {
      showMsg('error', 'Failed to register video. Check API connection.');
    }
  };

  const handleDelete = async (video) => {
    if (!video.id || !window.confirm(`Delete "${video.title || video.name}"?`)) return;
    setDeleting(video.id);
    try {
      await api.delete(`/videos/${video.id}`);
      setVideos(v => v.filter(x => x.id !== video.id));
      showMsg('success', 'Video deleted.');
    } catch {
      showMsg('error', 'Could not delete video.');
    } finally {
      setDeleting(null);
    }
  };

  const catColors = { GenAI: '#6366f1', Cloud: '#10b981', ML: '#f59e0b', DevOps: '#ec4899', Data: '#06b6d4', General: '#8b5cf6' };

  const iS = { width: '100%', background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10,
    padding: '10px 14px', color: '#fff', fontSize: '0.875rem',
    outline: 'none', boxSizing: 'border-box' };
  const lS = { display: 'block', fontSize: '0.72rem', color: C.textMuted,
    textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff' }}>Content Manager</div>
          <div style={{ fontSize: '0.8rem', color: C.textMuted, marginTop: 4 }}>
            Manage video content for learners
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => { if (activeTab === 'supabase') fetchSupabaseVideos(); else fetchDbVideos(); }}
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10, padding: '10px 16px', color: '#fff', fontWeight: 500, cursor: 'pointer', fontSize: '0.85rem' }}>
            ↺ Refresh
          </button>
          <button onClick={() => setShowForm(v => !v)} style={{
            background: showForm ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #6366f1, #10b981)',
            border: 'none', borderRadius: 10, padding: '10px 20px',
            color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
          }}>
            {showForm ? '✕ Cancel' : '+ Register Video'}
          </button>
        </div>
      </div>

      {/* Source tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20,
        background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[
          { key: 'supabase', label: '☁ Supabase Storage' },
          { key: 'db',       label: '🗄 Database Registry' },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '8px 18px', borderRadius: 8, border: 'none',
            background: activeTab === t.key ? 'linear-gradient(135deg,rgba(99,102,241,0.4),rgba(16,185,129,0.25))' : 'transparent',
            color: activeTab === t.key ? '#fff' : 'rgba(255,255,255,0.4)',
            fontWeight: activeTab === t.key ? 700 : 400, cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'inherit',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Status message */}
      {msg.text && (
        <div style={{ background: msg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${msg.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
          borderRadius: 10, padding: '12px 16px',
          color: msg.type === 'success' ? '#10b981' : '#ef4444',
          fontWeight: 600, fontSize: '0.875rem', marginBottom: 20 }}>
          {msg.type === 'success' ? '✅' : '⚠️'} {msg.text}
        </div>
      )}

      {/* Register form (DB only) */}
      {showForm && (
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, marginBottom: 24 }}>
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 4 }}>Register New Video (DB)</div>
          <div style={{ fontSize: '0.75rem', color: C.textMuted, marginBottom: 16 }}>
            Register a video from S3/storage by its key. For Supabase uploads, use the Supabase dashboard.
          </div>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
              <div><label style={lS}>Title *</label>
                <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Video title" required style={iS} /></div>
              <div><label style={lS}>Storage Key / Path *</label>
                <input value={form.s3Key} onChange={e => setForm(p => ({ ...p, s3Key: e.target.value }))}
                  placeholder="GenAI/intro-genai.mp4" required style={iS} /></div>
              <div><label style={lS}>Category</label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  style={{ ...iS, background: 'rgba(30,27,60,0.9)' }}>
                  <option>GenAI</option><option>Cloud</option><option>ML</option><option>DevOps</option><option>Data</option>
                </select></div>
              <div><label style={lS}>Level</label>
                <select value={form.level} onChange={e => setForm(p => ({ ...p, level: e.target.value }))}
                  style={{ ...iS, background: 'rgba(30,27,60,0.9)' }}>
                  <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
                </select></div>
              <div><label style={lS}>Duration</label>
                <input value={form.duration} onChange={e => setForm(p => ({ ...p, duration: e.target.value }))}
                  placeholder="e.g. 45 min" style={iS} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={lS}>Description</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe what learners will gain" rows={2}
                  style={{ ...iS, resize: 'vertical', fontFamily: 'inherit' }} /></div>
            </div>
            <button type="submit" style={{ background: 'linear-gradient(135deg, #6366f1, #10b981)',
              border: 'none', borderRadius: 10, padding: 12, color: '#fff',
              fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>Register Video</button>
          </form>
        </div>
      )}

      {/* Video list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
          <div style={{ color: C.textMuted, fontSize: '0.9rem' }}>Loading videos from {activeTab === 'supabase' ? 'Supabase Storage' : 'Database'}…</div>
        </div>
      ) : videos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎬</div>
          <div style={{ color: C.textMuted, marginBottom: 8 }}>
            {activeTab === 'supabase'
              ? 'No videos found in the Supabase "videos_blog" bucket.'
              : 'No videos registered in the database yet.'}
          </div>
          {activeTab === 'supabase' && (
            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.25)' }}>
              Upload .mp4/.webm/.mov files to the <code style={{ color: C.primary }}>videos_blog</code> bucket via Supabase dashboard.
            </div>
          )}
          {activeTab === 'db' && (
            <button onClick={() => setShowForm(true)} style={{ marginTop: 16, background: 'linear-gradient(135deg,#6366f1,#10b981)',
              border: 'none', borderRadius: 10, padding: '10px 22px', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
              + Register First Video
            </button>
          )}
        </div>
      ) : (
        <>
          <div style={{ fontSize: '0.75rem', color: C.textMuted, marginBottom: 12 }}>
            {videos.length} video{videos.length !== 1 ? 's' : ''} · {activeTab === 'supabase' ? 'Supabase Storage bucket "videos_blog"' : 'Database registry'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {videos.map((v, i) => {
              const color = catColors[v.category] || C.primary;
              return (
                <div key={v.id || v.key || i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`,
                  borderRadius: 12, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14,
                  transition: 'border-color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = color + '50'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = C.cardBorder}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: color + '20',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>▶</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#fff', fontSize: '0.9rem',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.title || v.name}</div>
                    <div style={{ fontSize: '0.72rem', color: C.textMuted, marginTop: 2 }}>
                      {[v.category, v.level, v.duration].filter(Boolean).join(' · ')}
                      {v.size ? ` · ${(v.size / 1024 / 1024).toFixed(1)} MB` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color,
                      background: color + '18', padding: '3px 10px', borderRadius: 20,
                      textTransform: 'uppercase', letterSpacing: 0.5 }}>{v.category || 'General'}</span>
                    {activeTab === 'supabase' && v.url && (
                      <a href={v.url} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '0.72rem', color: C.textMuted, background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                          padding: '4px 10px', textDecoration: 'none', fontWeight: 500 }}>Preview ↗</a>
                    )}
                    {activeTab === 'db' && v.id && (
                      <button onClick={() => handleDelete(v)} disabled={deleting === v.id}
                        style={{ fontSize: '0.72rem', color: '#ef4444', background: 'rgba(239,68,68,0.08)',
                          border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6,
                          padding: '4px 10px', cursor: 'pointer', fontWeight: 500 }}>
                        {deleting === v.id ? '…' : 'Delete'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ── Dashboard Shell ───────────────────────────────────────────────────────────

const ElitureDashboard = ({ user }) => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navItems = getNavItems(user?.role);

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':    return <DashboardHome user={user} />;
      case 'courses':      return <ElitureVideoPlayer user={user} />;
      case 'goals':        return <ElitureGoals />;
      case 'mocktest':     return <ElitureMockExam user={user} />;
      case 'analytics':    return <AnalyticsSection />;
      case 'certificates': return <CertificatesSection user={user} />;
      case 'profile':      return <ProfileSection user={user} />;
      case 'manage':       return <ContentManagerSection />;
      case 'admin':        return <ElitureAdminPanel />;
      default:             return <DashboardHome user={user} />;
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 68px)' }}>
      {/* Sidebar */}
      <aside style={{
        width: sidebarOpen ? 240 : 70, flexShrink: 0,
        background: 'rgba(15,12,41,0.7)', backdropFilter: 'blur(20px)',
        borderRight: '1px solid rgba(99,102,241,0.15)',
        padding: sidebarOpen ? '28px 16px' : '28px 10px',
        display: 'flex', flexDirection: 'column', gap: 4,
        transition: 'width 0.3s ease, padding 0.3s ease',
        position: 'sticky', top: 68, height: 'calc(100vh - 68px)',
        overflowY: 'auto', overflowX: 'hidden',
      }}>
        <button onClick={() => setSidebarOpen(v => !v)} style={{
          background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)',
          borderRadius: 8, padding: '6px 10px', color: 'rgba(255,255,255,0.6)',
          cursor: 'pointer', marginBottom: 20,
          alignSelf: sidebarOpen ? 'flex-end' : 'center', fontSize: 14,
        }}>
          {sidebarOpen ? '◀' : '▶'}
        </button>

        {navItems.map(item => {
          const isActive = activeSection === item.key;
          return (
            <button key={item.key} onClick={() => setActiveSection(item.key)} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: sidebarOpen ? '11px 14px' : '11px',
              borderRadius: 10, border: 'none',
              background: isActive
                ? 'linear-gradient(135deg, rgba(99,102,241,0.25), rgba(16,185,129,0.15))'
                : 'transparent',
              borderLeft: isActive ? '3px solid #6366f1' : '3px solid transparent',
              color: isActive ? '#fff' : 'rgba(255,255,255,0.5)',
              cursor: 'pointer', transition: 'all 0.2s', width: '100%',
              justifyContent: sidebarOpen ? 'flex-start' : 'center',
              fontWeight: isActive ? 600 : 400, fontSize: '0.875rem',
              whiteSpace: 'nowrap', overflow: 'hidden',
            }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
              {sidebarOpen && item.label}
            </button>
          );
        })}

        {/* Role badge at bottom */}
        {sidebarOpen && (
          <div style={{ marginTop: 'auto', background: 'rgba(99,102,241,0.1)',
            border: '1px solid rgba(99,102,241,0.15)', borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)',
              textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Signed in as</div>
            <div style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 600, marginBottom: 2,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.name}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.role === 'admin' ? 'Platform Admin' : user?.role === 'instructor' ? 'Instructor' : 'Learner'}
            </div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, padding: '32px', overflow: 'auto', minWidth: 0 }}>
        <div style={{ maxWidth: 1200 }}>
          {/* Breadcrumb */}
          <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>Eliture LMS</span>
            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.2)' }}>›</span>
            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
              {navItems.find(n => n.key === activeSection)?.label}
            </span>
          </div>

          {renderContent()}
        </div>
      </main>
    </div>
  );
};

export default ElitureDashboard;
